import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";

type LegalDocumentPageProps = {
  title: string;
  htmlUrl: string;
  defaultReturnPath?: string;
};

type LocationState = {
  returnTo?: string;
};

export function LegalDocumentPage({
  title,
  htmlUrl,
  defaultReturnPath = "/",
}: LegalDocumentPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;
    setContent(null);
    setError(null);

    const fetchContent = async () => {
      try {
        const response = await fetch(htmlUrl, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`Failed to load document (${response.status})`);
        }
        const text = await response.text();
        if (isActive) {
          setContent(text);
        }
      } catch (err) {
        if (isActive) {
          setError(err instanceof Error ? err.message : "Unable to load document.");
        }
      }
    };

    void fetchContent();

    return () => {
      isActive = false;
    };
  }, [htmlUrl]);

  const bodyHtml = useMemo(() => {
    if (!content) return "";
    const match = content.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (match) {
      return match[1];
    }
    return content;
  }, [content]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const previousTitle = document.title;
    document.title = `${title} | LullabAI`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  const handleClose = () => {
    if (state.returnTo) {
      navigate(state.returnTo, { replace: true });
      return;
    }
    if (location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate(defaultReturnPath, { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground safe-area-left safe-area-right">
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 safe-area-top">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3 md:px-8 md:py-4">
          <h1 className="text-lg font-semibold md:text-2xl">{title}</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label={`Close ${title}`}
            className="min-h-[44px] min-w-[44px]"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-8 md:py-10">
          {error ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-destructive">
              {error}
            </div>
          ) : content ? (
            <article
              className="prose prose-neutral max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          ) : (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
        </div>
      </main>
    </div>
  );
}

